import 'dayjs/locale/az';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { i18n as I18nInstance } from 'i18next';

import { DISPLAY_TIMEZONE, TIME_FORMAT } from '@/constants/common';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const { FULL_DATE_TIME, TIME_ONLY } = TIME_FORMAT;

/**
 * Format salary range: "1000 ₼" or "1000 - 2000 ₼"
 */
const formatSalary = (
  value: unknown,
  lng: string | undefined,
  i18n: I18nInstance
): string => {
  const { salaryFrom, salaryTo } = value as {
    salaryFrom: number;
    salaryTo?: number;
  };

  if (!salaryTo || salaryFrom === salaryTo) {
    return i18n.t('Card.salary_per_month', { lng, salary: salaryFrom });
  }

  return `${salaryFrom} - ${salaryTo} ₼`;
};

/**
 * Format age range: "от 25", "до 35", or "25-35"
 */
const formatAge = (
  value: unknown,
  lng: string | undefined,
  i18n: I18nInstance
): string => {
  const { ageFrom, ageTo } = value as { ageFrom?: number; ageTo?: number };

  if (!ageTo) {
    return i18n.t('Vacancy.age_from', { lng, age: ageFrom });
  }

  if (!ageFrom) {
    return i18n.t('Vacancy.age_to', { lng, age: ageTo });
  }

  return i18n.t('Vacancy.age_range', { lng, ageFrom, ageTo });
};

/**
 * Format date with relative time: "Сегодня, 14:30", "Вчера, 10:00", "3 дня назад", or full date
 */
const formatDate = (
  value: unknown,
  lng: string | undefined,
  i18n: I18nInstance,
  // Anchored to the server render time during SSR and the first client render
  // (passed via t(..., { now })), so relative dates do not cause a hydration
  // mismatch (React #425). Falls back to the live clock if not provided.
  nowMs?: number
): string => {
  dayjs.locale(lng);

  // Pin both sides to the same timezone so SSR (Vercel UTC) and the client
  // (user-local tz) format identically. Without this, `format('HH:mm')` and
  // `isSame(now, 'day')` diverge across the hydration boundary → React #425
  // (text mismatch) and, near midnight, #418 (today/yesterday branch flip).
  const date = dayjs(value as string | number | Date).tz(DISPLAY_TIMEZONE);
  const now = dayjs(nowMs ?? Date.now()).tz(DISPLAY_TIMEZONE);

  // Today
  if (date.isSame(now, 'day')) {
    const today = i18n.t('Card.today', { lng });
    return `${today}, ${date.format(TIME_ONLY)}`;
  }

  // Yesterday
  if (date.isSame(now.subtract(1, 'day'), 'day')) {
    const yesterday = i18n.t('Card.yesterday', { lng });
    return `${yesterday}, ${date.format(TIME_ONLY)}`;
  }

  // Within last 7 days - relative time anchored to `now` (NOT date.fromNow(),
  // which would use the live clock and break the anchor for this branch).
  if (date.isAfter(now.subtract(7, 'day'))) {
    return date.from?.(now) ?? date.format(FULL_DATE_TIME);
  }

  // Older - full date
  return date.format(FULL_DATE_TIME);
};

/**
 * i18next >= v24 resolves `{{value, formatterName}}` via formatter service.
 * Register our custom formatters explicitly so interpolation applies them.
 */
export const registerI18nFormatters = (i18n: I18nInstance) => {
  const { formatter } = i18n.services;
  if (!formatter) return;

  formatter.add('salaryFormat', (value, lng) => formatSalary(value, lng, i18n));
  formatter.add('ageFormat', (value, lng) => formatAge(value, lng, i18n));
  // The 3rd callback arg is the full t() options object; we forward `now` so
  // that SSR and the first client render produce identical relative dates
  // (anchor passed from <ServerNowProvider> via Card.tsx -> useStableNow()).
  formatter.add('customFormat', (value, lng, options) =>
    formatDate(value, lng, i18n, (options as { now?: number } | undefined)?.now)
  );
};
